/**
 * AdminStatsScreen.tsx
 * Page de statistiques et suivi des EDL — accessible aux admins
 *
 * @version 1.0.0 — tableaux + KPIs + filtres, données réelles Supabase
 */

import { useEffect, useState, useCallback } from 'react'
import { useAppStore } from '../store/useAppStore'
import Header from '../components/ui/Header'
import Card   from '../components/ui/Card'
import Button from '../components/ui/Button'
import toast  from 'react-hot-toast'
import {
  ChevronLeft, ChevronRight, Download, Search,
  BarChart3, AlertTriangle, CheckCircle2, Clock, Trash2
} from 'lucide-react'
import { buildAuthHeader } from '../utils/auth'
import { BACKEND } from '../utils/config'

// ── Types ─────────────────────────────────────────────────────────────────────
interface EdlRow {
  id:          string
  date:        string
  heure:       string
  agent:       string
  immat:       string
  contrat:     string
  type:        string
  dommages:    number
  statut:      string
  agence?:     string
  duree?:      number

}

// ── Helpers badges ─────────────────────────────────────────────────────────
function BadgeDmg({ n }: { n: number }) {
  if (n === 0) return <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-green-100 text-green-700">✅ 0</span>
  if (n <= 2)  return <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-orange-100 text-orange-700">⚠️ {n}</span>
  return             <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-700">🚨 {n}</span>
}

function BadgeStatut({ s }: { s: string }) {
  return s === 'depart' || s === 'Départ'
    ? <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-blue-100 text-blue-700">🚗 Départ</span>
    : <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-purple-100 text-purple-700">🏁 Retour</span>
}

function BadgeType({ t }: { t: string }) {
  const colors: Record<string, string> = {
    VL: 'bg-sky-100 text-sky-700',
    VU: 'bg-yellow-100 text-yellow-700',
    Électrique: 'bg-green-100 text-green-700',
    Frigo: 'bg-indigo-100 text-indigo-700',
  }
  return <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${colors[t] || 'bg-gray-100 text-gray-600'}`}>{t}</span>
}

// ── Composant principal ────────────────────────────────────────────────────
export default function AdminStatsScreen() {
  const credentials = useAppStore(s => s.credentials)
  const setStep     = useAppStore(s => s.setStep)

  const [data,     setData]     = useState<EdlRow[]>([])
  const [loading,  setLoading]  = useState(true)
  const [search,   setSearch]   = useState('')
  const [filterAgent,  setFilterAgent]  = useState('Tous')
  const [filterType,   setFilterType]   = useState('Tous')
  const [filterStatut, setFilterStatut] = useState('Tous')
  const [page,     setPage]     = useState(0)
  const [selected, setSelected] = useState<string | null>(null)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [isDeleting, setIsDeleting] = useState(false)

  const PAGE_SIZE = 20

  // ── Chargement données ─────────────────────────────────────────────────
  const fetchStats = useCallback(async () => {
    if (!credentials) return
    setLoading(true)
    try {
      const authHeader = buildAuthHeader(credentials)
      const res = await fetch(`${BACKEND}/api/admin/stats`, {
        headers: { 'X-MRC-Auth': authHeader },
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const json = await res.json()
      setData(json.edls ?? [])
    } catch (e) {
      console.error('[AdminStats] fetch:', e)
      toast.error('Erreur chargement statistiques')
      setData([])
    } finally {
      setLoading(false)
    }
  }, [credentials])

  useEffect(() => { fetchStats() }, [fetchStats])

  // ── Filtres ────────────────────────────────────────────────────────────
  const agents  = ['Tous', ...new Set(data.map(e => e.agent).filter(Boolean))]
  const types   = ['Tous', ...new Set(data.map(e => e.type).filter(Boolean))]
  const statuts = ['Tous', 'Départ', 'Retour']

  const filtered = data.filter(e => {
    const q = search.toLowerCase()
    const matchSearch = !q || 
      e.immat?.toLowerCase().includes(q) ||
      e.contrat?.toLowerCase().includes(q) ||
      e.agent?.toLowerCase().includes(q)
    const matchAgent  = filterAgent  === 'Tous' || e.agent  === filterAgent
    const matchType   = filterType   === 'Tous' || e.type   === filterType
    const matchStatut = filterStatut === 'Tous' || e.statut === filterStatut ||
      (filterStatut === 'Départ' && e.statut === 'depart') ||
      (filterStatut === 'Retour' && e.statut === 'retour')
    return matchSearch && matchAgent && matchType && matchStatut
  })

  // ── KPIs ───────────────────────────────────────────────────────────────
  const totalEDL      = filtered.length
  const totalDommages = filtered.reduce((s, e) => s + (e.dommages ?? 0), 0)
  const edlAvecDmg    = filtered.filter(e => (e.dommages ?? 0) > 0).length
  const tauxDmg       = totalEDL > 0 ? Math.round((edlAvecDmg / totalEDL) * 100) : 0
  
  // Calcul durée moyenne
  const dureeTotale = filtered.reduce((s, e) => s + (e.duree ?? 0), 0)
  const dureeMoyenneSec = totalEDL > 0 ? Math.floor(dureeTotale / totalEDL) : 0
  const moyMin = Math.floor(dureeMoyenneSec / 60)
  const moySec = dureeMoyenneSec % 60
  const dureeAffichage = `${moyMin}m ${moySec.toString().padStart(2, '0')}s`

  // ── Pagination ─────────────────────────────────────────────────────────
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const paged      = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE)

  // ── Export CSV ─────────────────────────────────────────────────────────
  const exportCSV = () => {
    const headers = ['Date', 'Heure', 'Agent', 'Immat', 'Contrat', 'Type', 'Statut', 'Dommages', 'Duree (Sec)']
    const rows = filtered.map(e => [e.date, e.heure, e.agent, e.immat, e.contrat, e.type, e.statut, e.dommages, e.duree ?? 0])
    const csv = [headers, ...rows].map(r => r.join(';')).join('\n')
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' })
    const url  = URL.createObjectURL(blob)
    const a    = document.createElement('a')
    a.href     = url
    a.download = `edl-stats-${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    URL.revokeObjectURL(url)
    toast.success('CSV exporté ✓')
  }

  // ── Suppression ────────────────────────────────────────────────────────
  const deleteSelected = async (ids: string[]) => {
    if (!credentials || ids.length === 0) return
    if (!confirm(`Supprimer ${ids.length} EDL(s) ? Cette action est irréversible.`)) return
    
    setIsDeleting(true)
    try {
      const authHeader = buildAuthHeader(credentials)
      const res = await fetch(`${BACKEND}/api/admin/stats/${ids.join(',')}`, {
        method: 'DELETE',
        headers: { 'X-MRC-Auth': authHeader },
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      
      toast.success(`${ids.length} EDL(s) supprimé(s)`)
      setSelectedIds(new Set())
      fetchStats()
    } catch (e) {
      console.error('[AdminStats] delete:', e)
      toast.error('Erreur lors de la suppression')
    } finally {
      setIsDeleting(false)
    }
  }

  const toggleOne = (e: React.ChangeEvent<HTMLInputElement>, id: string) => {
    e.stopPropagation()
    const next = new Set(selectedIds)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    setSelectedIds(next)
  }

  const toggleAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation()
    if (selectedIds.size === paged.length && paged.length > 0) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(paged.map(x => x.id)))
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex flex-col">
      <Header
        title="📋 Statistiques EDL"
        subtitle="Suivi et analyse des états des lieux"
        showBack
        backStep="sync"
      />

      <div className="flex-1 p-4 max-w-5xl mx-auto w-full flex flex-col gap-4">

        {/* KPIs */}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
          {[
            { label: 'EDL total',        value: totalEDL,      icon: <BarChart3 size={20} />,      color: 'bg-blue-50 border-blue-200 text-blue-700' },
            { label: 'Temps moyen',      value: dureeAffichage,icon: <Clock size={20} />,          color: 'bg-indigo-50 border-indigo-200 text-indigo-700' },
            { label: 'Total dommages',   value: totalDommages, icon: <AlertTriangle size={20} />,  color: 'bg-red-50 border-red-200 text-red-700' },
            { label: 'EDL avec dommage', value: edlAvecDmg,    icon: <BarChart3 size={20} />,          color: 'bg-orange-50 border-orange-200 text-orange-700' },
            { label: 'Taux dommages',    value: `${tauxDmg}%`, icon: <CheckCircle2 size={20} />,   color: 'bg-purple-50 border-purple-200 text-purple-700' },
          ].map(k => (
            <div key={k.label} className={`rounded-2xl border p-4 ${k.color}`}>
              <div className="mb-1">{k.icon}</div>
              <div className="text-2xl font-bold">{k.value}</div>
              <div className="text-xs font-medium mt-0.5 opacity-80">{k.label}</div>
            </div>
          ))}
        </div>

        {/* Filtres */}
        <div className="bg-white rounded-2xl shadow p-4 space-y-3">
          <p className="text-sm font-semibold text-gray-600 flex items-center gap-1.5">
            <Search size={14} /> Filtres
          </p>
          <div className="flex flex-wrap gap-3">
            <input
              type="text"
              placeholder="Rechercher immat, contrat, agent..."
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(0) }}
              className="flex-1 min-w-48 border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
            />
            <select value={filterAgent} onChange={e => { setFilterAgent(e.target.value); setPage(0) }}
              className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300">
              {agents.map(a => <option key={a}>{a}</option>)}
            </select>
            <select value={filterType} onChange={e => { setFilterType(e.target.value); setPage(0) }}
              className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300">
              {types.map(t => <option key={t}>{t}</option>)}
            </select>
            <select value={filterStatut} onChange={e => { setFilterStatut(e.target.value); setPage(0) }}
              className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300">
              {statuts.map(s => <option key={s}>{s}</option>)}
            </select>
            <button
              onClick={exportCSV}
              className="flex items-center gap-1.5 bg-blue-600 text-white px-3 py-2 rounded-xl text-sm font-semibold hover:bg-blue-700 transition"
            >
              <Download size={14} /> CSV
            </button>
            {selectedIds.size > 0 && (
              <button
                onClick={() => deleteSelected(Array.from(selectedIds))}
                disabled={isDeleting}
                className="flex items-center gap-1.5 bg-red-600 text-white px-3 py-2 rounded-xl text-sm font-semibold hover:bg-red-700 transition disabled:opacity-50"
              >
                <Trash2 size={14} /> {isDeleting ? '...' : `Supprimer (${selectedIds.size})`}
              </button>
            )}
          </div>
        </div>

        {/* Tableau */}
        {loading ? (
          <div className="flex-1 flex items-center justify-center py-20">
            <div className="w-8 h-8 rounded-full border-4 border-blue-500 border-t-transparent animate-spin" />
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    <th className="px-4 py-3 w-10 text-center">
                      <input 
                        type="checkbox" 
                        checked={paged.length > 0 && selectedIds.size === paged.length}
                        onChange={toggleAll}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer w-4 h-4"
                      />
                    </th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600">Date / Heure</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600">Agent</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600">Immat.</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600">Type</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600">N° Contrat</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600">Statut</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600 text-center">Durée</th>
                    <th className="text-center px-4 py-3 font-semibold text-gray-600">Dommages</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {paged.length === 0 && (
                    <tr>
                      <td colSpan={10} className="text-center py-10 text-gray-400">Aucun EDL trouvé</td>
                    </tr>
                  )}
                  {paged.map(e => (
                    <tr
                      key={e.id}
                      className={`border-b border-gray-50 hover:bg-blue-50 transition cursor-pointer ${selected === e.id ? 'bg-blue-50' : ''}`}
                      onClick={() => setSelected(selected === e.id ? null : e.id)}
                    >
                      <td className="px-4 py-3 text-center" onClick={ev => ev.stopPropagation()}>
                        <input 
                          type="checkbox"
                          checked={selectedIds.has(e.id)}
                          onChange={ev => toggleOne(ev, e.id)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer w-4 h-4"
                        />
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        <div className="font-medium">{e.date}</div>
                        <div className="text-xs text-gray-400">{e.heure}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold">
                            {(e.agent ?? '?').split(' ').map(n => n[0]).join('').slice(0, 2)}
                          </div>
                          <span className="text-gray-700">{e.agent}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 font-mono font-semibold text-gray-800">{e.immat}</td>
                      <td className="px-4 py-3"><BadgeType t={e.type} /></td>
                      <td className="px-4 py-3 text-gray-500 text-xs font-mono">{e.contrat}</td>
                      <td className="px-4 py-3"><BadgeStatut s={e.statut} /></td>
                      <td className="px-4 py-3 text-center text-xs font-mono text-gray-500 bg-gray-50/50">
                        {e.duree ? `${Math.floor(e.duree / 60)}m ${String(e.duree % 60).padStart(2,'0')}s` : '—'}
                      </td>
                      <td className="px-4 py-3 text-center"><BadgeDmg n={e.dommages ?? 0} /></td>
                      <td className="px-4 py-3 text-center" onClick={ev => ev.stopPropagation()}>
                        <button
                          onClick={() => deleteSelected([e.id])}
                          className="text-gray-400 hover:text-red-500 transition-colors p-1.5 rounded-lg hover:bg-red-50"
                          title="Supprimer"
                        >
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Footer tableau */}
            <div className="px-4 py-3 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
              <p className="text-xs text-gray-500">{filtered.length} EDL affichés</p>
              <div className="flex gap-1">
                <button
                  onClick={() => setPage(Math.max(0, page - 1))}
                  disabled={page === 0}
                  className="px-3 py-1 rounded-lg border border-gray-200 text-xs text-gray-600 hover:bg-gray-100 disabled:opacity-40"
                >
                  <ChevronLeft size={12} className="inline" /> Précédent
                </button>
                <span className="px-3 py-1 text-xs text-gray-500">{page + 1}/{totalPages}</span>
                <button
                  onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                  disabled={page >= totalPages - 1}
                  className="px-3 py-1 rounded-lg border border-gray-200 text-xs text-gray-600 hover:bg-gray-100 disabled:opacity-40"
                >
                  Suivant <ChevronRight size={12} className="inline" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
